import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

// Drives the Gift Templates gallery and a template's detail page: the
// create/duplicate/rename modal, the delete confirmation, and the
// available-for-all-teams toggle.
//
// The modals are still Bootstrap's jQuery plugin, so `$` here is the global
// jQuery the sprockets bundle sets up -- a webpack-imported jQuery would be a
// second instance without that plugin.
export default class extends Controller {
  static get targets() {
    return [
      "newModal",
      "deleteModal",
      "form",
      "modalTitle",
      "submitButton",
      "nameInput",
      "descriptionInput",
      "cloneIdInput",
      "cloneCollectionInput",
      "deleteMessage",
      "templateName",
      "templateDescription",
      "grid",
    ]
  }

  connect() {
    this.mode = "create"
    this.renamePath = null
    this.deletePath = null
    this.deleteRedirectPath = null
  }

  // The gallery's Create Template buttons: always a blank form.
  openNewModal(event) {
    event.preventDefault()
    this.resetModal()
    this.showModal()
  }

  duplicate(event) {
    event.preventDefault()

    this.resetModal()
    this.mode = "clone"
    this.modalTitleTarget.textContent = "Duplicate gift template"
    this.nameInputTarget.value = `Copy of ${this.templateName()}`
    this.descriptionInputTarget.value = this.templateDescription()
    this.cloneIdInputTarget.value = event.params.id
    // The detail page clones the price point it is showing, not the saved one.
    if (this.hasCloneCollectionInputTarget) {
      this.cloneCollectionInputTarget.value = event.params.collection || ""
    }
    this.submitButtonTarget.classList.remove("btn-disabled")
    this.showModal()
  }

  rename(event) {
    event.preventDefault()

    this.resetModal()
    this.mode = "rename"
    this.renamePath = event.params.path
    this.renameTrigger = event.currentTarget
    this.modalTitleTarget.textContent = "Rename gift template"
    this.submitButtonTarget.value = "Rename Template"
    this.submitButtonTarget.classList.remove("btn-disabled")
    this.nameInputTarget.value = this.templateName()
    this.descriptionInputTarget.value = this.templateDescription()
    // The form is built for a new template, so the reset left its default here;
    // saving that would change the template's sharing behind the reader's back.
    this.setAvailableForTeams(event.params.availableForTeams)
    this.showModal()
  }

  // Creating and cloning both post the form; only renaming is handled here.
  submitModal(event) {
    if (this.mode !== "rename") {
      return
    }

    event.preventDefault()

    const body = {
      gift_template: {
        name: this.nameInputTarget.value,
        description: this.descriptionInputTarget.value,
        available_for_teams: this.availableForTeamsChecked(),
      },
    }

    fetch(this.renamePath, this.jsonOptions(RequestHelper.defaultPatchOptions(), body))
      .then((response) => this.parse(response))
      .then((data) => {
        // Written back rather than reloaded: a Turbo visit here would wipe
        // the toast that confirms the rename, and the page is reading these two
        // elements as its own record of the name.
        this.templateNameTarget.textContent = data.gift_template.name
        this.templateDescriptionTarget.textContent = data.gift_template.description
        this.renameTrigger.dataset.giftTemplatesAvailableForTeamsParam = data.gift_template.available_for_teams
        $(this.newModalTarget).modal("hide")
        window.ltAlert({ message: data.message })
      })
      .catch((error) => window.ltAlert({ message: error.message, type: "toast-error" }))
  }

  validateName() {
    this.submitButtonTarget.classList.toggle("btn-disabled", this.nameInputTarget.value.length === 0)
  }

  // Asks the server whether the template is wired into a live automation before
  // saying what deleting it will cost.
  confirmDelete(event) {
    event.preventDefault()

    this.deletePath = event.params.path
    this.deleteRedirectPath = event.params.redirect || null

    this.deleteMessageTarget.innerHTML =
      "Are you sure you want to delete this template?<br/><br/>This action can not be undone."
    $(this.deleteModalTarget).modal("show")

    // The detail page serves HTML at this URL, and Rails reads the default
    // Accept header as browser-like, so ask for JSON by extension.
    fetch(`${this.deletePath}.json`, RequestHelper.defaultRequestOptions())
      .then((response) => this.parse(response))
      .then((data) => {
        if (data.hasLinkedAutomations) {
          this.deleteMessageTarget.innerHTML =
            "Are you sure you want to delete this template? This template is currently being used in Automations, all automations using this template will stop and will resume when the template is replaced.<br/><br/>This action can not be undone."
        }
      })
      .catch((error) => {
        $(this.deleteModalTarget).modal("hide")
        window.ltAlert({ message: error.message, type: "toast-error" })
      })
  }

  destroy(event) {
    event.preventDefault()

    const button = event.currentTarget
    button.classList.add("disabled")

    fetch(this.deletePath, RequestHelper.defaultDeleteOptions())
      .then((response) => this.parse(response))
      .then((data) => {
        button.classList.remove("disabled")
        $(this.deleteModalTarget).modal("hide")

        // The detail page cannot stay put once its template is gone, and the
        // gallery needs a reload only when its last card just went away.
        if (this.deleteRedirectPath) {
          // The visit swaps out the body the toast is appended to, so it has to
          // wait for the page it lands on.
          document.addEventListener("turbo:load", () => window.ltAlert({ message: data.message }), { once: true })
          Turbo.visit(this.deleteRedirectPath)
          return
        }

        window.ltAlert({ message: data.message })

        if (this.hasGridTarget && this.gridTarget.children.length === 1) {
          // Keep the query string: dropping it would bounce the user off the
          // My Templates tab onto the Loop & Tie gallery.
          Turbo.visit(window.location.pathname + window.location.search)
        } else {
          const card = this.element.querySelector(`[data-id='${data.external_id}']`)
          if (card) {
            card.remove()
          }
        }
      })
      .catch((error) => {
        button.classList.remove("disabled")
        window.ltAlert({ message: error.message, type: "toast-error" })
      })
  }

  toggleAvailableForTeams(event) {
    if (event.target.tagName !== "INPUT") {
      return
    }

    const body = { gift_template: { available_for_teams: event.target.checked } }

    fetch(event.params.path, this.jsonOptions(RequestHelper.defaultPatchOptions(), body))
      .then((response) => this.parse(response))
      .then((data) => window.ltAlert({ message: data.message }))
      .catch((error) => window.ltAlert({ message: error.message, type: "toast-error" }))
  }

  resetModal() {
    this.mode = "create"
    this.renamePath = null
    this.modalTitleTarget.textContent = "Create new gift template"
    this.formTarget.reset()
    this.cloneIdInputTarget.value = ""
    if (this.hasCloneCollectionInputTarget) {
      this.cloneCollectionInputTarget.value = ""
    }
    this.submitButtonTarget.value = "Create Template"
    this.submitButtonTarget.classList.add("btn-disabled")
  }

  showModal() {
    $(this.newModalTarget).modal("show")
  }

  // The rendered page is the source of truth for both, so a rename does not
  // leave the duplicate prefill quoting the old name.
  templateName() {
    return this.hasTemplateNameTarget ? this.templateNameTarget.textContent.trim() : ""
  }

  templateDescription() {
    return this.hasTemplateDescriptionTarget ? this.templateDescriptionTarget.textContent.trim() : ""
  }

  availableForTeamsChecked() {
    const checkbox = this.availableForTeamsCheckbox()

    return checkbox ? checkbox.checked : false
  }

  setAvailableForTeams(available) {
    const checkbox = this.availableForTeamsCheckbox()

    if (checkbox) {
      checkbox.checked = available === true
    }
  }

  availableForTeamsCheckbox() {
    return this.formTarget.querySelector("#gift_template_available_for_teams")
  }

  jsonOptions(options, body) {
    return Object.assign(options, {
      headers: Object.assign({}, options.headers, { "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    })
  }

  // Errors come back as JSON too, and their message is what the toast shows.
  parse(response) {
    return response.json().then((data) => {
      if (!response.ok) {
        throw new Error(data.message || "Something went wrong. Please try again.")
      }

      return data
    })
  }
}
